// Collapsible left sidebar with folder tree, nav links, and footer.
// Context-aware: swaps header/list based on current route.

import React, { useState, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSessionContext } from '../../contexts/SessionContext';
import { useGraphStorageContext } from '../../contexts/GraphStorageContext';
import { useTheme } from '../../contexts/ThemeContext';
import FolderTree from './FolderTree';
import SavedGraphList from './SavedGraphList';
import ChatItemMenu from './ChatItemMenu';
import GraphItemMenu from './GraphItemMenu';
import FolderMenu from './FolderMenu';
import ClearAllDialog from './ClearAllDialog';
import {
  chatToMarkdownPlain,
  chatToMarkdownDetailed,
  triggerDownload,
  sanitizeFilename,
  formatDate,
} from '../../lib/export';
import fkieLogo from '../../assets/fraunhofer-fkie-logo.png';
import fkieLogoDark from '../../assets/fraunhofer-fkie-logo-dark.png';

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

type ActiveRoute = 'home' | 'chat' | 'graph' | 'overview' | 'explore' | 'trends' | 'docs';

function resolveRoute(pathname: string): ActiveRoute {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/overview')) return 'overview';
  if (pathname.startsWith('/graph')) return 'graph';
  if (pathname.startsWith('/explore')) return 'explore';
  if (pathname.startsWith('/trends')) return 'trends';
  if (pathname.startsWith('/docs')) return 'docs';
  return 'home';
}

export default function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolved } = useTheme();
  const activeRoute = resolveRoute(location.pathname);

  // Chat context
  const ctx = useSessionContext();
  const {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
    deleteSession,
    renameSession,
    clearAllSessions,
    folders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveSessionToFolder,
    folderCollapseState,
    toggleFolderCollapse,
    openExportDialog,
  } = ctx;

  // Graph context
  const graphCtx = useGraphStorageContext();
  const {
    savedGraphs,
    activeGraphId,
    deleteGraph,
    renameGraph,
    setActiveGraphId,
    clearAllGraphs,
  } = graphCtx;

  // Inline rename state — supports session, folder, and graph
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingType, setRenamingType] = useState<'session' | 'folder' | 'graph' | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Context menu state
  const [chatMenuState, setChatMenuState] = useState<{ sessionId: string; rect: DOMRect } | null>(null);
  const [folderMenuState, setFolderMenuState] = useState<{ folderId: string; rect: DOMRect } | null>(null);
  const [graphMenuState, setGraphMenuState] = useState<{ graphId: string; rect: DOMRect } | null>(null);

  // Clear-all confirmation
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // ── Rename helpers ───────────────────────────────────────────────────

  const startRenameSession = useCallback((id: string, currentTitle: string) => {
    setRenamingId(id);
    setRenamingType('session');
    setRenameValue(currentTitle || 'New chat');
  }, []);

  const startRenameFolder = useCallback((id: string, currentName: string) => {
    setRenamingId(id);
    setRenamingType('folder');
    setRenameValue(currentName);
  }, []);

  const startRenameGraph = useCallback((id: string, currentTitle: string) => {
    setRenamingId(id);
    setRenamingType('graph');
    setRenameValue(currentTitle || 'Untitled graph');
  }, []);

  const commitRename = useCallback(() => {
    if (renamingId && renameValue.trim()) {
      if (renamingType === 'session') {
        renameSession(renamingId, renameValue.trim());
      } else if (renamingType === 'folder') {
        renameFolder(renamingId, renameValue.trim());
      } else if (renamingType === 'graph') {
        renameGraph(renamingId, renameValue.trim());
      }
    }
    setRenamingId(null);
    setRenamingType(null);
    setRenameValue('');
  }, [renamingId, renamingType, renameValue, renameSession, renameFolder, renameGraph]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenamingType(null);
    setRenameValue('');
  }, []);

  // ── Download helpers ─────────────────────────────────────────────────

  const downloadChat = useCallback((sessionId: string, detailed: boolean) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;
    const md = detailed ? chatToMarkdownDetailed(session) : chatToMarkdownPlain(session);
    const filename = `${sanitizeFilename(session.title)}-${formatDate()}.md`;
    triggerDownload(md, filename);
  }, [sessions]);

  // ── Folder creation ──────────────────────────────────────────────────

  const handleCreateFolder = useCallback(() => {
    createFolder('New Folder');
  }, [createFolder]);

  // ── Switch session + navigate ────────────────────────────────────────

  const handleSwitchSession = useCallback((id: string) => {
    switchSession(id);
    navigate('/chat');
  }, [switchSession, navigate]);

  // ── Switch graph + navigate ──────────────────────────────────────────

  const handleSwitchGraph = useCallback((id: string) => {
    setActiveGraphId(id);
    navigate(`/graph?savedGraphId=${id}`);
  }, [setActiveGraphId, navigate]);

  // ── Clear all (route-aware) ──────────────────────────────────────────

  const handleClearAll = useCallback(() => {
    if (activeRoute === 'graph') {
      clearAllGraphs();
    } else {
      clearAllSessions();
    }
    setConfirmClearAll(false);
  }, [activeRoute, clearAllGraphs, clearAllSessions]);

  const clearAllLabel = activeRoute === 'graph' ? 'saved graphs' : 'chats';

  return (
    <aside className={`malpedia-sidebar${collapsed ? ' collapsed' : ''}`}>
      <button
        className="malpedia-sidebar-toggle"
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="4" x2="15" y2="4" />
          <line x1="3" y1="9" x2="15" y2="9" />
          <line x1="3" y1="14" x2="15" y2="14" />
        </svg>
      </button>

      <div className="malpedia-sidebar-inner">
        {/* Navigation */}
        <nav className="malpedia-sidebar-nav">
          <NavLink
            to="/"
            end
            data-page="home"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 8l6-5 6 5v6a1 1 0 0 1-1 1h-3v-4H6v4H3a1 1 0 0 1-1-1z" />
            </svg>
            Home
          </NavLink>
          <NavLink
            to="/chat"
            data-page="chat"
            onClick={() => {
              if (activeRoute !== 'chat') createSession();
            }}
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5l-3 3z" />
            </svg>
            Chat
          </NavLink>
          <NavLink
            to="/graph"
            data-page="graph"
            onClick={() => {
              setActiveGraphId(null);
            }}
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="4" cy="4" r="2" />
              <circle cx="12" cy="4" r="2" />
              <circle cx="8" cy="12" r="2" />
              <line x1="5.5" y1="5.5" x2="7" y2="10.5" />
              <line x1="10.5" y1="5.5" x2="9" y2="10.5" />
            </svg>
            Graph
          </NavLink>
          <NavLink
            to="/overview"
            data-page="overview"
            style={{ display: 'none' }}
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="4" cy="4" r="2" />
              <circle cx="12" cy="4" r="2" />
              <circle cx="4" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <line x1="6" y1="4" x2="10" y2="4" />
              <line x1="6" y1="12" x2="10" y2="12" />
              <line x1="4" y1="6" x2="4" y2="10" />
              <line x1="12" y1="6" x2="12" y2="10" />
            </svg>
            Overview
          </NavLink>
          <NavLink
            to="/explore"
            data-page="explore"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="7" r="4.5" />
              <line x1="10.5" y1="10.5" x2="14" y2="14" />
            </svg>
            Explore
          </NavLink>
          <NavLink
            to="/trends"
            data-page="trends"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,12 5,8 8,10 13,3" />
              <polyline points="10,3 13,3 13,6" />
            </svg>
            Trends
          </NavLink>
          <NavLink
            to="/docs"
            data-page="docs"
            className={({ isActive }) =>
              `malpedia-sidebar-nav-item${isActive ? ' active' : ''}`
            }
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h4.5a2 2 0 0 1 2 2v9a1.5 1.5 0 0 0-1.5-1.5H2z" />
              <path d="M14 3H9.5a2 2 0 0 0-2 2v9A1.5 1.5 0 0 1 9 12.5H14z" />
            </svg>
            Docs
          </NavLink>
        </nav>

        {/* Session / graph list: route-dependent */}
        <div className="malpedia-sidebar-sessions">
          {activeRoute === 'chat' && (
            <FolderTree
              sessions={sessions}
              folders={folders}
              activeSessionId={activeSessionId}
              folderCollapseState={folderCollapseState}
              renamingId={renamingType === 'session' || renamingType === 'folder' ? renamingId : null}
              renamingType={renamingType === 'session' || renamingType === 'folder' ? renamingType : null}
              renameValue={renameValue}
              onToggleFolderCollapse={toggleFolderCollapse}
              onSwitchSession={handleSwitchSession}
              onStartRenameSession={startRenameSession}
              onStartRenameFolder={startRenameFolder}
              onRenameValueChange={setRenameValue}
              onCommitRename={commitRename}
              onCancelRename={cancelRename}
              onChatMenuOpen={(sessionId, rect) => {
                setFolderMenuState(null);
                setGraphMenuState(null);
                setChatMenuState({ sessionId, rect });
              }}
              onFolderMenuOpen={(folderId, rect) => {
                setChatMenuState(null);
                setGraphMenuState(null);
                setFolderMenuState({ folderId, rect });
              }}
              onChatDownload={(sessionId) => downloadChat(sessionId, false)}
              onCreateFolder={handleCreateFolder}
              onMoveSessionToFolder={moveSessionToFolder}
            />
          )}
          {activeRoute === 'graph' && (
            <SavedGraphList
              graphs={savedGraphs}
              activeGraphId={activeGraphId}
              renamingId={renamingType === 'graph' ? renamingId : null}
              renameValue={renameValue}
              onSwitchGraph={handleSwitchGraph}
              onStartRename={startRenameGraph}
              onRenameValueChange={setRenameValue}
              onCommitRename={commitRename}
              onCancelRename={cancelRename}
              onGraphMenuOpen={(graphId, rect) => {
                setChatMenuState(null);
                setFolderMenuState(null);
                setGraphMenuState({ graphId, rect });
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="malpedia-sidebar-footer">
          {(activeRoute === 'chat' || activeRoute === 'graph') && (
            <button className="malpedia-sidebar-clear-all" onClick={() => setConfirmClearAll(true)}>
              Clear All
            </button>
          )}
          <img
            src={resolved === 'dark' ? fkieLogoDark : fkieLogo}
            alt="Fraunhofer FKIE"
            className="malpedia-sidebar-fkie-logo"
          />
        </div>
      </div>

      {/* Chat context menu */}
      {chatMenuState && (() => {
        const session = sessions.find((s) => s.id === chatMenuState.sessionId);
        if (!session) return null;
        return (
          <ChatItemMenu
            sessionId={chatMenuState.sessionId}
            sessionFolderId={session.folderId}
            anchorRect={chatMenuState.rect}
            folders={folders}
            onRename={() => startRenameSession(chatMenuState.sessionId, session.title)}
            onMoveToFolder={(folderId) => moveSessionToFolder(chatMenuState.sessionId, folderId)}
            onDownloadPlain={() => downloadChat(chatMenuState.sessionId, false)}
            onDownloadDetailed={() => downloadChat(chatMenuState.sessionId, true)}
            onDelete={() => deleteSession(chatMenuState.sessionId)}
            onClose={() => setChatMenuState(null)}
          />
        );
      })()}

      {/* Folder context menu */}
      {folderMenuState && (() => {
        const folder = folders.find((f) => f.id === folderMenuState.folderId);
        if (!folder) return null;
        const chatCount = sessions.filter((s) => s.folderId === folder.id).length;
        return (
          <FolderMenu
            folderId={folderMenuState.folderId}
            folderName={folder.name}
            chatCount={chatCount}
            anchorRect={folderMenuState.rect}
            onRename={() => startRenameFolder(folderMenuState.folderId, folder.name)}
            onExportFolder={() => openExportDialog({ type: 'folder', folderId: folderMenuState.folderId })}
            onDelete={() => deleteFolder(folderMenuState.folderId)}
            onClose={() => setFolderMenuState(null)}
          />
        );
      })()}

      {/* Graph context menu */}
      {graphMenuState && (() => {
        const g = savedGraphs.find((sg) => sg.id === graphMenuState.graphId);
        if (!g) return null;
        return (
          <GraphItemMenu
            anchorRect={graphMenuState.rect}
            onRename={() => startRenameGraph(graphMenuState.graphId, g.title)}
            onDelete={() => deleteGraph(graphMenuState.graphId)}
            onClose={() => setGraphMenuState(null)}
          />
        );
      })()}

      {/* Clear all confirmation dialog */}
      {confirmClearAll && (
        <ClearAllDialog
          onConfirm={handleClearAll}
          onClose={() => setConfirmClearAll(false)}
          label={clearAllLabel}
        />
      )}
    </aside>
  );
}
